import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { CHOROPLETH_COLORS } from './constants';
import { ChoroplethResponse } from './types';

interface ChoroplethLayerProps {
  data: ChoroplethResponse | null;
  onSA2Click: (properties: any) => void;
}

function getColor(value: number | null, min: number, max: number): string {
  if (value === null || isNaN(value)) return '#ccc';
  if (max === min) return CHOROPLETH_COLORS[2];
  const ratio = (value - min) / (max - min);
  const idx = Math.min(Math.floor(ratio * CHOROPLETH_COLORS.length), CHOROPLETH_COLORS.length - 1);
  return CHOROPLETH_COLORS[idx];
}

export default function ChoroplethLayer({ data, onSA2Click }: ChoroplethLayerProps) {
  const map = useMap();
  const layerRef = useRef<L.GeoJSON | null>(null);

  useEffect(() => {
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }

    if (!data?.features?.length || !data.meta) return;

    const { min, max } = data.meta;

    const geoLayer = L.geoJSON(data as any, {
      style: (feature: any) => {
        const val = feature?.properties?.metric_value;
        return {
          fillColor: getColor(val, min, max),
          color: '#2e873d',
          weight: 2,
          fillOpacity: 0.55,
        };
      },
      onEachFeature: (feature: any, layer: L.Layer) => {
        const props = feature.properties || {};
        const val = props.metric_value;
        const formatted = val !== null && val !== undefined ? Number(val).toLocaleString() : 'N/A';

        (layer as any).bindTooltip(
          `<strong>${props.sa2_name}</strong><br/>${formatted}`,
          { direction: 'top', sticky: true }
        );

        layer.on('mouseover', (e: any) => {
          e.target.setStyle({ fillOpacity: 0.75, weight: 3 });
        });
        layer.on('mouseout', (e: any) => {
          geoLayer.resetStyle(e.target);
        });
        layer.on('click', () => {
          onSA2Click(props);
        });
      },
    });

    layerRef.current = geoLayer;
    geoLayer.addTo(map);

    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map, data, onSA2Click]);

  return null;
}
